import { Box, Typography, TextField, Button, Grid, Autocomplete } from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { CompanyFormData } from '../../../../types/organization';
import { TIMEZONES } from '../../../../types/timezone';

interface CompanyFormProps {
  data: CompanyFormData;
  onUpdate: (field: string, value: any) => void;
  onNext: () => void;
  onBack: () => void;
}

export default function CompanyForm({ data, onUpdate, onNext, onBack }: CompanyFormProps) {
  const { control, handleSubmit } = useForm({
    defaultValues: data,
  });

  const onSubmit = (formData: CompanyFormData) => {
    onUpdate('company', formData);
    onNext();
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Typography variant="h6" sx={{ mb: 3 }}>
        Company Details
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Controller
            name="name"
            control={control}
            rules={{ required: 'Company name is required' }}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                label="Company Name"
                fullWidth
                error={!!error}
                helperText={error?.message}
              />
            )}
          />
        </Grid>
        <Grid item xs={12}>
          <Controller
            name="description"
            control={control}
            render={({ field }) => (
              <TextField {...field} label="Description" fullWidth multiline rows={3} />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Controller
            name="website"
            control={control}
            render={({ field }) => <TextField {...field} label="Website" fullWidth />}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <Controller
            name="phone"
            control={control}
            render={({ field }) => (
              <TextField
                type="tel"
                {...field}
                label="Phone (Optional)"
                fullWidth
                inputProps={{
                  inputMode: 'tel',
                }}
              />
            )}
          />
        </Grid>
        <Grid item xs={12}>
          <Controller
            name="address"
            control={control}
            rules={{ required: 'Address is required' }}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                label="Address"
                fullWidth
                error={!!error}
                helperText={error?.message}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Controller
            name="city"
            control={control}
            rules={{ required: 'City is required' }}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                label="City"
                fullWidth
                error={!!error}
                helperText={error?.message}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Controller
            name="state"
            control={control}
            rules={{ required: 'State is required' }}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                label="State"
                fullWidth
                error={!!error}
                helperText={error?.message}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Controller
            name="country"
            control={control}
            rules={{ required: 'Country is required' }}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                label="Country"
                fullWidth
                error={!!error}
                helperText={error?.message}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Controller
            name="postcode"
            control={control}
            rules={{
              required: 'Postcode is required',
              pattern: {
                value: /^[A-Za-z0-9\s-]{3,10}$/, // Matches alphanumeric, spaces, or hyphens (between 3 to 10 characters)
                message: 'Invalid postcode format',
              },
            }}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                label="Postcode"
                fullWidth
                error={!!error}
                helperText={error?.message}
              />
            )}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <Controller
            name="timezone"
            control={control}
            rules={{ required: 'Timezone is required' }}
            render={({ field, fieldState: { error } }) => (
              <Autocomplete
                {...field}
                options={TIMEZONES}
                getOptionLabel={option => option}
                renderInput={params => (
                  <TextField
                    {...params}
                    label="Timezone"
                    fullWidth
                    error={!!error}
                    helperText={error?.message}
                  />
                )}
                onChange={(event, value) => field.onChange(value)}
              />
            )}
          />
        </Grid>
      </Grid>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
        <Button onClick={onBack}>Back</Button>
        <Button type="submit" variant="contained" disableElevation sx={{ minWidth: 120 }}>
          Next
        </Button>
      </Box>
    </form>
  );
}
